import React, {Dispatch, FC, SetStateAction, useContext, useEffect, useMemo, useState} from 'react';
import {CustomCollapse} from "../../../../../../components/UI/AntCustom/CustomCollapse";
import {CustomPanel} from "../../../../../../components/UI/AntCustom/CustomPanel";
import {Col, Form, Radio, Row} from "antd";
import {FlyHourPriceCalculatorSubgroup} from "./FlyHourPriceCalculatorSubgroup";
import styled from "styled-components";
import {CustomFieldInput} from "../../../../../../components/UI/AntCustom/CustomFieldInput";
import {CustomButton} from "../../../../../../components/UI/AntCustom/CustomButton";
import {defineMessages, useIntl} from "react-intl";
import {CommonButtonMessages} from "../../../../../../intl/commonMessages/CommonButtonMessages";
import {Logger} from "../../../../../../utils/logger/Logger";
import {getErrorMessage} from "../../../../../../utils/errorHelpers/getCommonErrorMessage";
import {FlyHourPriceContext} from "./context/FlyHourPriceContextProvider";
import {message} from "antd";
import {
  IFlyHourCalculatorCategory,
  IFlyHourCalculatorSubCategory
} from "../../../../../../store/reducers/fleetReducer/fleetReducerTypes";
import {FlyHourPriceDefaultSettingTypeEnum} from "./types/flyHourPriceSettingEnums";
import {onFinishTrimmed} from "../../../../../../utils/helpers/formHelpers/onFinishTrimmed";
import {RenderConfirmModal} from "../../../../../../components/UI/StatusModals/renderSuccessModal";
import {GroupFormOptionKeys} from "./GroupFormOptionKeys";

const GroupContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px 24px 16px 24px;
`;

const RadioButtonsWrapper = styled.div`
  margin-bottom: 24px;
`;

const SubcategoriesWrapper = styled.div<{isCoefficientCategory?: boolean}>`
  display: ${props => props.isCoefficientCategory ? 'grid' : 'flex'};
  flex-direction: ${props => props.isCoefficientCategory ? 'unset' : 'column'};
  grid-template-columns: ${props => props.isCoefficientCategory ? '1fr 1fr' : 'unset'};
  grid-template-rows: ${props => props.isCoefficientCategory ? 'auto auto' : 'unset'};
  gap: 16px;
  margin-bottom: 16px;
  
  ${props => props.isCoefficientCategory && `
    & > *:nth-child(1) {
      grid-column: 1;
      grid-row: 1;
    }
    & > *:nth-child(2) {
      grid-column: 2;
      grid-row: 1;
    }
    & > *:nth-child(3) {
      grid-column: 1;
      grid-row: 2;
    }
    & > *:nth-child(4) {
      grid-column: 2;
      grid-row: 2;
    }
  `}
`;

const CategoryHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const SavedText = styled.div`
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  color: #808080;
`;


const FlyHourPriceCalculatorGroupMessages = defineMessages({
  saved: {
    id: "flyHourPriceCalculatorGroup.saved",
    defaultMessage: "Saved",
  },
  resetConfirm: {
    id: "flyHourPriceCalculatorGroup.resetConfirm",
    defaultMessage: "Are you sure you want to clear all fields in this section?",
  }
})


interface GroupFormOptions {
  key: GroupFormOptionKeys;
  title: string;
  subcategories: IFlyHourCalculatorSubCategory[];
}

interface IFlyHourPriceCalculatorGroup {
  isBlocked: boolean
  category: IFlyHourCalculatorCategory;
  setSavedCategory: Dispatch<SetStateAction<IFlyHourCalculatorCategory>>;
  groupFormOptions: GroupFormOptions[];
  isFieldsCountChangeable: boolean;
}

export const FlyHourPriceCalculatorGroup: FC<IFlyHourPriceCalculatorGroup> = ({
                                                                                isBlocked,
                                                                                category,
                                                                                setSavedCategory,
                                                                                groupFormOptions,
                                                                                isFieldsCountChangeable,
                                                                              }) => {
  const intl = useIntl();
  const [form] = Form.useForm();
  
  // Determine default selection: if SEPARATELY has values, use it, otherwise use TOTAL_SUM
  const defaultFormOption = useMemo((): GroupFormOptionKeys => {
    const separatelyOption = groupFormOptions.find(o => o.key === GroupFormOptionKeys.SEPARATELY);
    if (separatelyOption) {
      // Check if SEPARATELY has any values (including additional fields like fuelGallonsPerHour, fromHours, etc.)
      const hasSeparatelyValues = separatelyOption.subcategories.some(sc => 
        sc.settingTypes.some(st => {
          const hasValue = st.setting.value !== undefined && st.setting.value !== null && st.setting.value !== 0;
          const hasAdditionalFields = 
            st.setting.fuelGallonsPerHour !== undefined ||
            st.setting.fromHours !== undefined ||
            st.setting.toHours !== undefined ||
            st.setting.dateFrom !== undefined ||
            st.setting.dateTo !== undefined;
          return hasValue || hasAdditionalFields;
        })
      );
      if (hasSeparatelyValues) {
        return GroupFormOptionKeys.SEPARATELY;
      }
    }
    // Default to TOTAL_SUM if no values in SEPARATELY or if SEPARATELY option doesn't exist
    return groupFormOptions.find(o => o.key === GroupFormOptionKeys.TOTAL_SUM)?.key ?? groupFormOptions.find(o => !!o)?.key ?? GroupFormOptionKeys.TOTAL_SUM;
  }, [groupFormOptions, category]);
  
  const [selectedFormOption, setSelectedFormOption] = useState<GroupFormOptionKeys>(defaultFormOption);
  const [hasUserManuallySelected, setHasUserManuallySelected] = useState<boolean>(false);
  const {categoriesSaving, setCategoriesSaving, setIsMarginTaxesDisabled} = useContext(FlyHourPriceContext);
  const [wasSavedOnce, setWasSavedOnce] = useState<boolean>(false);
  const [isFormTouched, setIsFormTouched] = useState<boolean>(false);
  const [canBeReset, setCanBeReset] = useState<boolean>(true);
  const [isFirstRender, setIsFirstRender] = useState<boolean>(true);
  
  // Update selectedFormOption when category data first loads (e.g., from backend)
  // But only if user hasn't manually selected an option yet
  useEffect(() => {
    if (!hasUserManuallySelected && defaultFormOption !== selectedFormOption) {
      setSelectedFormOption(defaultFormOption);
    }
  }, [defaultFormOption, hasUserManuallySelected, selectedFormOption]);

  const hasInitialFilledFields = useMemo(() => groupFormOptions.find(o => o.key === selectedFormOption)?.subcategories.some(sc => sc.settingTypes.some(st => st.setting.value !== 0)), [selectedFormOption, category]);

  useEffect(() => {
    const defaultValuesMap = groupFormOptions
      .map(g => g.subcategories).flat()
      .map(sc => sc.settingTypes).flat()
      .flatMap(st => {
        const values: Array<{key: string, defaultValue: number | undefined}> = [
          {key: st.keyName, defaultValue: st.setting.value === 0 ? undefined : st.setting.value}
        ];
        // Add fuelGallonsPerHour field if it exists
        if (st.setting.fuelGallonsPerHour !== undefined) {
          values.push({
            key: `${st.keyName}_fuelGallonsPerHour`,
            defaultValue: st.setting.fuelGallonsPerHour
          });
        }
        return values;
      });

    let defaultValues: { [key: string]: number | undefined } = {}
    defaultValuesMap.forEach(v => {
      defaultValues[v.key] = v.defaultValue;
    })

    form.setFieldsValue(defaultValues);
  }, [])

  useEffect(() => {
    isFirstRender
      ? setIsFirstRender(false)
      : changeIsCategorySaved(false);

    setCanBeReset(Object.values(form.getFieldsValue()).some(v => v !== undefined));
  }, [selectedFormOption])

  const changeIsCategorySaved = (isSaved: boolean) => {
    setCategoriesSaving(prev => [...prev.map(c => {return c.categoryName !== category.keyName ? c : {...c, isSaved: isSaved}})]);
  }

  const getIsCategorySaved = () => {
    return categoriesSaving.find(c => c.categoryName === category.keyName)?.isSaved;
  }

  const onFinish = (values: { [key: string]: number | undefined }) => {
    try {
      // Валидация блоков маржи перед сохранением
      const marginErrors = validateMarginBlocks();
      if (marginErrors.length > 0) {
        message.error(marginErrors.join('; '));
        return;
      }

      // Fill gaps
      const filledValues: { [key: string]: number } = {};
      for (const [key, value] of Object.entries(values)) {
        filledValues[key] = value ?? 0;
      }

      // Set values into categoryObject and update it for outer control
      const resultCategory: IFlyHourCalculatorCategory = {
        ...category,
        subcategories: category.subcategories.map(sc => {
          return {
            ...sc, settingTypes: sc.settingTypes.map(st => {
              // Для поля топлива, значение берем из fuelGallonsPerHour, иначе из обычного поля
              const fuelKey = `${st.keyName}_fuelGallonsPerHour`;
              const isFuelField = st.keyName === FlyHourPriceDefaultSettingTypeEnum.PARAMETER_CHANGEABLE_FUEL_GALLONS_PER_HOUR;
              
              let settingValue: number | undefined;
              if (isFuelField && (values.hasOwnProperty(fuelKey) || st.setting.fuelGallonsPerHour !== undefined)) {
                // Для топлива используем fuelGallonsPerHour как основное значение
                settingValue = values.hasOwnProperty(fuelKey) 
                  ? values[fuelKey] 
                  : st.setting.fuelGallonsPerHour;
              } else {
                // Для остальных полей используем обычную логику
                settingValue = filledValues.hasOwnProperty(st.keyName) ? filledValues[st.keyName] : st.setting.value;
              }
              
              // Сохраняем дополнительные поля для блоков маржи, сезонных коэффициентов и топлива
              const additionalFields: any = {};
              
              // Поля для блоков маржи
              if (st.setting.fromHours !== undefined) {
                const fromHoursKey = `${st.keyName}_fromHours`;
                additionalFields.fromHours = values[fromHoursKey] ?? st.setting.fromHours;
              }
              if (st.setting.toHours !== undefined) {
                const toHoursKey = `${st.keyName}_toHours`;
                additionalFields.toHours = values[toHoursKey] ?? st.setting.toHours;
              }
              
              // Поля для сезонных коэффициентов (полные даты YYYY-MM-DD)
              if (st.setting.dateFrom !== undefined) {
                const dateFromKey = `${st.keyName}_dateFrom`;
                const dateFromValue: any = values[dateFromKey];
                if (dateFromValue) {
                  if (typeof dateFromValue === 'string') {
                    additionalFields.dateFrom = dateFromValue;
                  } else if (dateFromValue && typeof dateFromValue === 'object' && 'format' in dateFromValue && typeof (dateFromValue as any).format === 'function') {
                    additionalFields.dateFrom = (dateFromValue as any).format('YYYY-MM-DD');
                  } else {
                    additionalFields.dateFrom = st.setting.dateFrom;
                  }
                } else {
                  additionalFields.dateFrom = st.setting.dateFrom;
                }
              }
              if (st.setting.dateTo !== undefined) {
                const dateToKey = `${st.keyName}_dateTo`;
                const dateToValue: any = values[dateToKey];
                if (dateToValue) {
                  if (typeof dateToValue === 'string') {
                    additionalFields.dateTo = dateToValue;
                  } else if (dateToValue && typeof dateToValue === 'object' && 'format' in dateToValue && typeof (dateToValue as any).format === 'function') {
                    additionalFields.dateTo = (dateToValue as any).format('YYYY-MM-DD');
                  } else {
                    additionalFields.dateTo = st.setting.dateTo;
                  }
                } else {
                  additionalFields.dateTo = st.setting.dateTo;
                }
              }
              
              // Поле для топлива - всегда проверяем форму, даже если значение было undefined
              if (isFuelField) {
                if (values.hasOwnProperty(fuelKey)) {
                  // Если значение есть в форме, используем его (может быть undefined для очистки)
                  additionalFields.fuelGallonsPerHour = values[fuelKey];
                } else if (st.setting.fuelGallonsPerHour !== undefined) {
                  // Если значения нет в форме, но было в настройках, сохраняем старое
                  additionalFields.fuelGallonsPerHour = st.setting.fuelGallonsPerHour;
                }
              }
              
              return {
                ...st,
                setting: {
                  ...st.setting,
                  value: settingValue,
                  ...additionalFields
                }
              };
            })
          }
        })
      }

      setSavedCategory(resultCategory);
      setIsFormTouched(false);
      setWasSavedOnce(true);
      changeIsCategorySaved(true);
    } catch (e) {
      Logger.error(getErrorMessage(e));
    }
  }

  const validateMarginBlocks = (): string[] => {
    const errors: string[] = [];
    const marginBlocks: Array<{fromHours: number, toHours: number | undefined, keyName: string}> = [];
    
    // Собираем все блоки маржи
    category.subcategories.forEach(sc => {
      sc.settingTypes.forEach(st => {
        if (st.keyName === FlyHourPriceDefaultSettingTypeEnum.PARAMETER_MARGIN_AMOUNT 
          || st.keyName.startsWith(FlyHourPriceDefaultSettingTypeEnum.PARAMETER_MARGIN_AMOUNT + '_')) {
          const fromHours = form.getFieldValue(`${st.keyName}_fromHours`) ?? st.setting.fromHours ?? 0;
          const toHours = form.getFieldValue(`${st.keyName}_toHours`) ?? st.setting.toHours;
          marginBlocks.push({
            fromHours: fromHours ?? 0,
            toHours: toHours ?? undefined,
            keyName: st.keyName
          });
        }
      });
    });

    if (marginBlocks.length === 0) return errors;

    // Проверка 1: Максимум 7 блоков
    if (marginBlocks.length > 7) {
      errors.push(intl.formatMessage({id: "flyHourPriceCalculator.maxMarginBlocks", defaultMessage: "Maximum 7 margin blocks"}));
    }

    // Сортируем блоки по fromHours
    const sortedBlocks = [...marginBlocks].sort((a, b) => (a.fromHours ?? 0) - (b.fromHours ?? 0));

    // Проверка 2: Первый блок должен начинаться с 0
    if (sortedBlocks.length > 0 && sortedBlocks[0].fromHours > 0) {
      errors.push(intl.formatMessage({id: "flyHourPriceCalculator.marginValidation.firstBlock", defaultMessage: "First margin block must start at 0"}));
    }

    // Проверка 3: Последний блок должен покрывать до бесконечности (toHours должен быть undefined или очень большим)
    if (sortedBlocks.length > 0 && sortedBlocks[sortedBlocks.length - 1].toHours !== undefined && sortedBlocks[sortedBlocks.length - 1].toHours !== null) {
      errors.push(intl.formatMessage({id: "flyHourPriceCalculator.marginValidation.lastBlock", defaultMessage: "Last margin block must cover to infinity (leave toHours empty)"}));
    }

    // Проверка 4: Непересекающиеся диапазоны и отсутствие пробелов
    for (let i = 0; i < sortedBlocks.length - 1; i++) {
      const current = sortedBlocks[i];
      const next = sortedBlocks[i + 1];
      const currentToHours = current.toHours ?? Number.MAX_SAFE_INTEGER;
      const nextFromHours = next.fromHours ?? 0;

      if (currentToHours > nextFromHours) {
        errors.push(intl.formatMessage({id: "flyHourPriceCalculator.marginValidation.overlap", defaultMessage: `Margin blocks overlap: block ending at ${currentToHours} overlaps with block starting at ${nextFromHours}`}));
      }
      if (currentToHours < nextFromHours) {
        errors.push(intl.formatMessage({id: "flyHourPriceCalculator.marginValidation.gap", defaultMessage: `Gap between margin blocks: from ${currentToHours} to ${nextFromHours}`}));
      }
    }

    return errors;
  };

  const onValuesChange = (values: any) => {
    const fieldName = Object.keys(values)[0];
    if ([FlyHourPriceDefaultSettingTypeEnum.PARAMETER_MARGIN_AMOUNT.toString()].includes(fieldName)) {
      const marginValue: number | undefined = form.getFieldValue(FlyHourPriceDefaultSettingTypeEnum.PARAMETER_MARGIN_AMOUNT.toString());
      setIsMarginTaxesDisabled(!marginValue);

      if (!marginValue)
        form.setFieldsValue({[FlyHourPriceDefaultSettingTypeEnum.PARAMETER_MARGIN_TAXES.toString()]: undefined});
    }

    // Валидация блоков маржи при изменении значений
    if (fieldName.includes('_fromHours') || fieldName.includes('_toHours')) {
      const errors = validateMarginBlocks();
      if (errors.length > 0) {
        // Показываем ошибки через form.setFields
        form.setFields(errors.map((error, index) => ({
          name: fieldName,
          errors: [error]
        })));
      } else {
        form.setFields([{name: fieldName, errors: []}]);
      }
    }

    changeIsCategorySaved(false);
    if (!isFormTouched)
      setIsFormTouched(true);
  }

  const onReset = () => {
    const resetFields = () => {
      changeIsCategorySaved(false);
      setCanBeReset(false);
      setIsFormTouched(true);
      form.resetFields();

      // Автоматически делаем submit после сброса, чтобы сохранить сброшенные значения
      setTimeout(() => {
        form.submit();
      }, 0);
    };

    RenderConfirmModal(
      intl.formatMessage(FlyHourPriceCalculatorGroupMessages.resetConfirm),
      resetFields,
      intl
    );
  }

  const currentSubcategories = useMemo(() => groupFormOptions.find(o => o.key === selectedFormOption)?.subcategories, [selectedFormOption, category]);
  const isOnlyOneSubcategory = useMemo(() => currentSubcategories?.length === 1, [currentSubcategories]);

  const radioButtons = groupFormOptions.length > 1
    ? <RadioButtonsWrapper>
      {groupFormOptions.map(o => <Radio key={o.key}
                                        value={o.key}
                                        checked={selectedFormOption === o.key}
                                        onClick={() => {
                                          setHasUserManuallySelected(true);
                                          setSelectedFormOption(o.key);
                                        }}>{o.title}</Radio>)}
    </RadioButtonsWrapper>
    : undefined;

  const isCoefficientCategory = category.keyName === 'category_coefficient';
  const subcategoriesComponents = currentSubcategories && <SubcategoriesWrapper isCoefficientCategory={isCoefficientCategory}>
    {currentSubcategories?.map(sc => <FlyHourPriceCalculatorSubgroup key={sc.keyName}
                                                                     category={category}
                                                                     subcategory={sc}
                                                                     isOnlyOne={isOnlyOneSubcategory}
                                                                     isFieldsCountChangeable={isFieldsCountChangeable}/>)}
  </SubcategoriesWrapper>

  const categoryHeader = <CategoryHeader>
    <div>{category.label}</div>
    {wasSavedOnce && getIsCategorySaved() && <SavedText>{intl.formatMessage(FlyHourPriceCalculatorGroupMessages.saved)}</SavedText>}
  </CategoryHeader>

  return (
    <CustomCollapse expandIconPosition={'end'} borderRadius={'all'} size={'l'}
                    style={{background: "#fff", width: 866}} collapsible={isBlocked ? "disabled" : undefined}>
      <CustomPanel
        key={category.keyName}
        header={categoryHeader}
        background={"#fff"}
        noPaddings={true}
      >
        <GroupContent>
          {radioButtons}
          <Form
            form={form}
            layout={'vertical'}
            onFinish={(values) => onFinishTrimmed(values, onFinish)}
            onValuesChange={onValuesChange}
            onReset={onReset}
          >
            {subcategoriesComponents}

            <Row justify={'end'} gutter={10}>
              <Col>
                <CustomButton type={'primary'}
                              ghost
                              onClick={onReset}
                              disabled={!hasInitialFilledFields && !canBeReset}>
                  {intl.formatMessage(CommonButtonMessages.reset)}
                </CustomButton>
              </Col>
            </Row>
          </Form>
        </GroupContent>
      </CustomPanel>
    </CustomCollapse>
  );
};
